import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axiApi from "../ApiCaller";
import i18n from "i18next";
import ImageChooser from "components/ImageChooser";
import { SwalAlertClass } from "core/swalClass";

const Profile = () => {
    const { t: tC } = useTranslation("common");
    const { t: tA } = useTranslation("authenticate");
    const { user, updateUser } = useContext(AuthContext);
    const profileFormRef = useRef(null);
    const imageChooserContainerRef = useRef(null);
    const navigate = useNavigate();
    const Alert = new SwalAlertClass({ confirmButtonText: "Aceptar" });

    const [formData, setFormData] = useState({ ...user });
    const [originalData, setOriginalData] = useState({ ...user });
    const [userToken, setUserToken] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [avatarChanged, setAvatarChanged] = useState(true);
    const [resetImageKey, setResetImageKey] = useState(0);

    // --- 2FA state ---
    const [tfaActive, setTfaActive] = useState(false);
    const [tfaType, setTfaType] = useState(null);     // tipo actual en BD: "app" | "email" | null
    const [tfaSelected, setTfaSelected] = useState(""); // lo que el usuario ha elegido en el select
    const [tfaView, setTfaView] = useState("idle");   // "idle" | "setup-app" | "setup-email"
    const [tfaQr, setTfaQr] = useState(null);
    const [tfaCode, setTfaCode] = useState("");
    const [tfaError, setTfaError] = useState(false);
    const [tfaLoading, setTfaLoading] = useState(false);
    const [tfaResendMsg, setTfaResendMsg] = useState("");
    // disable flow
    const [tfaShowDisable, setTfaShowDisable] = useState(false);
    const [tfaDisableCode, setTfaDisableCode] = useState("");
    const [tfaDisableError, setTfaDisableError] = useState(false);
    const [tfaDisableLoading, setTfaDisableLoading] = useState(false);
    const [tfaDisableSent, setTfaDisableSent] = useState(false);

    const fetchTfaStatus = useCallback(async () => {
        try {
            const { data } = await axiApi.get("/api/two-factor/status/");
            setTfaActive(Boolean(data.is_active));
            setTfaType(data.type || null);
            // Pre-seleccionar el tipo actual en el select
            setTfaSelected(data.type || "");
        } catch {
            // ignorar
        }
    }, []);

    const resetTfaFlow = () => {
        setTfaView("idle");
        setTfaQr(null);
        setTfaCode("");
        setTfaError(false);
        setTfaResendMsg("");
        setTfaLoading(false);
        setTfaShowDisable(false);
        setTfaDisableCode("");
        setTfaDisableError(false);
        setTfaDisableLoading(false);
        setTfaDisableSent(false);
    };

    const handleStartTfaSetup = async () => {
        if (!tfaSelected) return;
        setTfaLoading(true);
        setTfaError(false);
        setTfaResendMsg("");
        try {
            const { data } = await axiApi.get(`/api/two-factor/setup/?type=${tfaSelected}&change=true`);
            if (tfaSelected === "app") {
                setTfaQr(data.qr);
                setTfaView("setup-app");
            } else {
                setTfaView("setup-email");
            }
        } catch {
            setTfaError(true);
        } finally {
            setTfaLoading(false);
        }
    };

    const handleConfirmTfa = async (e) => {
        e && e.preventDefault();
        if (!tfaCode) { setTfaError(true); return; }
        setTfaLoading(true);
        setTfaError(false);
        try {
            const { data } = await axiApi.post("/api/two-factor/confirm/", {
                code: tfaCode,
                type: tfaSelected,
            });
            if (data && data.status) {
                await fetchTfaStatus();
                resetTfaFlow();
            } else {
                setTfaError(true);
            }
        } catch {
            setTfaError(true);
        } finally {
            setTfaLoading(false);
        }
    };

    const handleResendTfaEmail = async () => {
        try {
            await axiApi.get(`/api/two-factor/setup/?type=email&change=true`);
            setTfaResendMsg(tA("messages.email-resent"));
        } catch {
            setTfaError(true);
        }
    };

    const handleStartDisable = async () => {
        setTfaDisableError(false);
        if (tfaType === "email") {
            try {
                await axiApi.get("/api/two-factor/send-code/");
                setTfaDisableSent(true);
            } catch {
                setTfaDisableError(true);
                return;
            }
        }
        setTfaShowDisable(true);
    };

    const handleConfirmDisable = async (e) => {
        e.preventDefault();
        if (!tfaDisableCode) { setTfaDisableError(true); return; }
        setTfaDisableLoading(true);
        setTfaDisableError(false);
        try {
            const { data } = await axiApi.delete("/api/two-factor/disable/", {
                data: { code: tfaDisableCode }
            });
            if (data.status) {
                await fetchTfaStatus();
                resetTfaFlow();
            } else {
                setTfaDisableError(true);
            }
        } catch {
            setTfaDisableError(true);
        } finally {
            setTfaDisableLoading(false);
        }
    };
    // --- fin 2FA ---

    useEffect(() => {
        fetchTfaStatus();
    }, [fetchTfaStatus]);

    useEffect(() => {
        if (user && user.token) {
            setUserToken(user.token);
        } else {
            axiApi.get('/api/profiles/')
                .then(resp => {
                    if (resp.data && resp.data.status) {
                        if (resp.data.user_token) {
                            setUserToken(resp.data.user_token);
                        }
                    } else {
                        console.error("No se pudo obtener el token de usuario");
                    }
                })
                .catch(error => {
                    console.error("Error fetching user token:", error);
                });
        }
    }, [user]);

    const navigateToOrganization = orgToken => navigate(`/administration/organizations?orgToken=${orgToken}`);

    const handleFieldChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAvatarChange = () => setAvatarChanged(true);

    const handleCancel = () => {
        setFormData(originalData);
        setAvatarChanged(false);
        setResetImageKey(prev => prev + 1);
    };

    const handleSave = () => {
        if (isSubmitting || !userToken) return;

        setIsSubmitting(true);

        const profileData = new FormData(profileFormRef.current);
        profileData.append('token', userToken);

        if (!avatarChanged && formData.avatar) {
            profileData.append('keep_existing_avatar', "true");
        }

        axiApi.post(`/api/profile/edit/`, profileData, {
            headers: { "Content-Type": "multipart/form-data" }
        })
            .then((r) => {
                if (r.data && r.data.status !== false) {
                    if (formData.language && formData.language !== i18n.language) {
                        i18n.changeLanguage(formData.language);
                    }

                    if (updateUser) {
                        const updatedUser = { ...user, ...formData };

                        const hasNewAvatar = Boolean(r.data.item?.avatar);
                        const avatarWasRemoved = avatarChanged && !r.data.item?.avatar;

                        if (hasNewAvatar) {
                            updatedUser.avatar = r.data.item.avatar;
                        } else if (avatarWasRemoved) {
                            updatedUser.avatar = null;
                        }

                        updateUser(updatedUser);
                    }

                    setOriginalData({ ...formData, avatar: r.data.item.avatar });
                    Alert.success("Guardado", "Se ha guardado correctamente")
                    setAvatarChanged(false);
                } else {
                    Alert.warning("Error", r.data.message);
                }
                setIsSubmitting(false);
            })
            .catch((error) => {
                console.error("Error guardando perfil:", error);
                Alert.error("Error en el servidor", "Informar al administrador")
                setIsSubmitting(false);
            });
    };

    return (
        <div className="container-fluid p-0">
            <div className="preview-canvas" >
                <div className="d-flex align-items-center">
                    <div className="preview-icon" >
                        <FontAwesomeIcon icon="fa-user" />
                    </div>
                    <div className="preview-title">
                        <h5 style={{ paddingLeft: "1rem" }} className="mb-0">{tC("profile.profile")} - {formData.first_name} {formData.last_name}</h5>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="container-fluid mt-4">
                    <form className="row pb-5" ref={profileFormRef}>
                        <div className="col-md-4">
                            <h5 className="mb-0 text-primary">{tC("user.avatar")}</h5>
                            <hr className="mt-0" />

                            <div className="text-center mt-4" ref={imageChooserContainerRef}>
                                <ImageChooser
                                    key={resetImageKey}
                                    name="avatar"
                                    image={formData.avatar ? "/media/" + formData.avatar : null}
                                    onChange={handleAvatarChange}
                                />
                            </div>

                            <h5 className="mb-0 text-primary mt-4">{tC("user.aditional")}</h5>
                            <hr className="mt-0" />

                            <div className="mt-4">
                                <h6>{tC("profile.organizations")}</h6>
                                {user.allowed_orgs?.map((item, idx) => (
                                    <div key={idx} className="mt-2 d-flex align-items-center justify-content-between">
                                        <span>{item.name}</span>
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => navigateToOrganization(item.token)}
                                            title="Ver detalle de la organización"
                                            type="button"
                                        >
                                            <FontAwesomeIcon icon="file-export" />
                                        </button>
                                    </div>
                                ))}

                                <h6 className="mt-4">{tC("profile.municipalities")}</h6>
                                {user.allowed_panels?.map((item, idx) => (
                                    <div key={idx} className="mt-2">{item.name}</div>
                                ))}
                            </div>
                        </div>

                        <div className="col-md-8">
                            <h5 className="mb-0 text-primary">{tC("user.profile")}</h5>
                            <hr className="mt-0" />

                            {[
                                {
                                    id: "first_name",
                                    label: tC("profile.name"),
                                    type: "text",
                                    value: formData.first_name
                                },
                                {
                                    id: "last_name",
                                    label: tC("profile.surname"),
                                    type: "text",
                                    value: formData.last_name
                                },
                                {
                                    id: "email",
                                    label: "Email",
                                    type: "email",
                                    value: formData.email,
                                    alwaysReadOnly: true
                                },
                                {
                                    id: "phone_1",
                                    label: tC("profile.phone"),
                                    type: "number",
                                    value: formData.phone_1
                                },
                                {
                                    id: "phone_2",
                                    label: tC("profile.mobile"),
                                    type: "number",
                                    value: formData.phone_2
                                }
                            ].map((field) => (
                                <div className="row mt-4" key={field.id}>
                                    <div className="col-md-3 d-flex align-items-center justify-content-end">
                                        <label htmlFor={field.id} className="form-label mb-0">{field.label}:</label>
                                    </div>
                                    <div className="col-md-9">
                                        <input
                                            type={field.type}
                                            name={field.id}
                                            id={field.id}
                                            value={field.value || ""}
                                            onChange={field.alwaysReadOnly ? undefined : handleFieldChange}
                                            readOnly={field.alwaysReadOnly}
                                            className="form-control"
                                        />
                                    </div>
                                </div>
                            ))}

                            <div className="row mt-4">
                                <div className="col-md-3 d-flex align-items-center justify-content-end">
                                    <label htmlFor="language" className="form-label mb-0">Idioma:</label>
                                </div>
                                <div className="col-md-9">
                                    <select
                                        name="language"
                                        id="language"
                                        value={formData.language || ""}
                                        className="form-select"
                                        onChange={handleFieldChange}
                                    >
                                        <option value="es">{tC("profile.spanish")}</option>
                                        <option value="en">{tC("profile.english")}</option>
                                    </select>
                                </div>
                            </div>

                            {/* ---- Verificación en dos pasos ---- */}
                            <div className="row mt-4">
                                <div className="col-md-3 d-flex align-items-start justify-content-end pt-2">
                                    <label className="form-label mb-0">{tA("title.otp")}:</label>
                                </div>
                                <div className="col-md-9">

                                    {/* Selector de método */}
                                    {tfaView === "idle" && (
                                        <div>
                                            {tfaActive && (
                                                <div className="mb-2">
                                                    <span className="badge bg-success me-2">{tA("labels.otp-active")}</span>
                                                    <span className="text-muted small">
                                                        {tfaType === "email" ? tA("buttons.otp-type-email") : tA("buttons.otp-type-app")}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="d-flex gap-2 align-items-center flex-wrap">
                                                <select
                                                    className="form-select"
                                                    style={{ maxWidth: 260 }}
                                                    value={tfaSelected}
                                                    onChange={(e) => { setTfaSelected(e.target.value); setTfaError(false); }}
                                                >
                                                    <option value="">{tA("labels.otp-inactive")}</option>
                                                    <option value="app">{tA("buttons.otp-type-app")}</option>
                                                    <option value="email">{tA("buttons.otp-type-email")}</option>
                                                </select>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-primary"
                                                    onClick={handleStartTfaSetup}
                                                    disabled={tfaLoading || !tfaSelected || tfaSelected === tfaType}
                                                >
                                                    {tfaLoading ? "..." : (tfaActive ? tA("buttons.otp-change-method") : tA("buttons.otp-activate"))}
                                                </button>
                                                {tfaActive && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={handleStartDisable}
                                                    >
                                                        {tA("buttons.otp-disable")}
                                                    </button>
                                                )}
                                            </div>
                                            {tfaError && (
                                                <div className="text-danger small mt-1">{tA("messages.error-server")}</div>
                                            )}
                                            {tfaActive && tfaShowDisable && (
                                                <div className="mt-2 border rounded p-2 bg-light">
                                                    {tfaType === "email" && tfaDisableSent && (
                                                        <p className="mb-2 small text-success">{tA("messages.email-sent")}</p>
                                                    )}
                                                    <div className="d-flex gap-2 align-items-center flex-wrap">
                                                        <input
                                                            type="text"
                                                            className={`form-control form-control-sm ${tfaDisableError ? "is-invalid" : ""}`}
                                                            style={{ maxWidth: 140 }}
                                                            placeholder={tA("labels.otp")}
                                                            maxLength={6}
                                                            value={tfaDisableCode}
                                                            onChange={(e) => { setTfaDisableCode(e.target.value); setTfaDisableError(false); }}
                                                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleConfirmDisable(); } }}
                                                            autoFocus
                                                        />
                                                        <button type="button" className="btn btn-sm btn-danger" onClick={handleConfirmDisable} disabled={tfaDisableLoading}>
                                                            {tA("buttons.otp-disable-confirm")}
                                                        </button>
                                                        <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setTfaShowDisable(false); setTfaDisableCode(""); setTfaDisableError(false); }}>
                                                            {tA("buttons.cancel")}
                                                        </button>
                                                    </div>
                                                    {tfaDisableError && (
                                                        <div className="text-danger small mt-1">{tA("messages.error-otp")}</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Setup app (QR) */}
                                    {tfaView === "setup-app" && tfaQr && (
                                        <div>
                                            <p className="mb-2 small">{tA("title.otp-setup-desc")}</p>
                                            <img src={tfaQr} alt="QR 2FA" style={{ width: 140, height: 140 }} className="d-block mb-2" />
                                            <div className="d-flex gap-2 align-items-center flex-wrap">
                                                <input
                                                    type="text"
                                                    className={`form-control form-control-sm ${tfaError ? "is-invalid" : ""}`}
                                                    style={{ maxWidth: 140 }}
                                                    placeholder={tA("labels.otp")}
                                                    maxLength={6}
                                                    value={tfaCode}
                                                    onChange={(e) => { setTfaCode(e.target.value); setTfaError(false); }}
                                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleConfirmTfa(); } }}
                                                    autoFocus
                                                />
                                                <button type="button" className="btn btn-sm btn-primary" onClick={handleConfirmTfa} disabled={tfaLoading}>
                                                    {tA("buttons.verify-otp")}
                                                </button>
                                                <button type="button" className="btn btn-sm btn-secondary" onClick={resetTfaFlow}>
                                                    {tA("buttons.cancel")}
                                                </button>
                                            </div>
                                            {tfaError && <div className="text-danger small mt-1">{tA("messages.error-otp")}</div>}
                                        </div>
                                    )}

                                    {/* Setup email */}
                                    {tfaView === "setup-email" && (
                                        <div>
                                            <p className="mb-2 small">{tA("title.otp-setup-email-desc")}</p>
                                            {tfaResendMsg && <div className="text-success small mb-1">{tfaResendMsg}</div>}
                                            <div className="d-flex gap-2 align-items-center flex-wrap">
                                                <input
                                                    type="text"
                                                    className={`form-control form-control-sm ${tfaError ? "is-invalid" : ""}`}
                                                    style={{ maxWidth: 140 }}
                                                    placeholder={tA("labels.otp")}
                                                    maxLength={6}
                                                    value={tfaCode}
                                                    onChange={(e) => { setTfaCode(e.target.value); setTfaError(false); }}
                                                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleConfirmTfa(); } }}
                                                    autoFocus
                                                />
                                                <button type="button" className="btn btn-sm btn-primary" onClick={handleConfirmTfa} disabled={tfaLoading}>
                                                    {tA("buttons.verify-otp")}
                                                </button>
                                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleResendTfaEmail}>
                                                    {tA("buttons.resend-code")}
                                                </button>
                                                <button type="button" className="btn btn-sm btn-secondary" onClick={resetTfaFlow}>
                                                    {tA("buttons.cancel")}
                                                </button>
                                            </div>
                                            {tfaError && <div className="text-danger small mt-1">{tA("messages.error-otp")}</div>}
                                        </div>
                                    )}

                                </div>
                            </div>
                            {/* ---- fin 2FA ---- */}
                        </div>

                        <div className="text-end pb-3 mt-4">
                            <>
                                <button
                                    type="button"
                                    className="btn btn-primary me-2"
                                    onClick={handleSave}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Guardando...' : 'Aceptar'}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary ms-2"
                                    onClick={handleCancel}
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </button>
                            </>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Profile;